<template>
  <v-card>
    <v-card-title>{{ $t('auth.changePassword') }}</v-card-title>

    <template v-if="changedPassword">
      <v-card-text>
        <v-alert type="success" dense text v-t="$t('auth.passwordChanged')"></v-alert>
      </v-card-text>
    </template>
    <template v-else>
      <v-card-text>
        <v-alert v-if="errorMessage" type="error" dense text>{{ errorMessage }}</v-alert>
      </v-card-text>
      <v-card-text>
        <v-form ref="form" autocomplete="off" v-model="valid" @submit.prevent="submit">
          <!-- Email obligatorio en primer login (usuario creado desde admin con @pendiente.local) -->
          <v-col v-if="mustSetEmail" cols="12">
            <v-alert type="info" dense text class="mb-3">
              Completá tu correo electrónico. Lo vas a usar para recuperar contraseña y notificaciones.
            </v-alert>
            <v-text-field
              v-model="form.email"
              type="email"
              prepend-icon="mdi-email"
              :label="$t('user.label.email')"
              :placeholder="$t('user.label.email')"
              :rules="emailRules"
              autocomplete="email"
              color="secondary"
            />
          </v-col>

          <v-col cols="12">
            <v-text-field
              id="current-password"
              prepend-icon="lock"
              :append-icon="showCurrentPassword ? 'visibility' : 'visibility_off'"
              :type="showCurrentPassword ? 'text' : 'password'"
              @click:append="showCurrentPassword = !showCurrentPassword"
              v-model="form.currentPassword"
              :rules="requiredRule"
              :label="$t('user.label.currentPassword')"
              :placeholder="$t('user.label.currentPassword')"
              autocomplete="new-password"
              color="secondary"
            />
          </v-col>
          <v-col cols="12">
            <v-text-field
              id="new-password"
              prepend-icon="lock"
              :append-icon="showNewPassword ? 'visibility' : 'visibility_off'"
              :type="showNewPassword ? 'text' : 'password'"
              @click:append="showNewPassword = !showNewPassword"
              v-model="form.newPassword"
              :rules="newPasswordRules"
              :label="$t('user.label.newPassword')"
              :placeholder="$t('user.label.newPassword')"
              autocomplete="new-password"
              color="secondary"
            />
            <div class="password-requirements mt-2" v-if="form.newPassword != null && String(form.newPassword).trim() !== ''">
              <div class="caption mb-1">{{ $t('auth.passwordRequirements') }}:</div>
              <div class="requirement-line" :class="{ 'requirement-ok': passwordChecks.minLength }">
                <v-icon x-small :color="passwordChecks.minLength ? 'success' : 'grey'">
                  {{ passwordChecks.minLength ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                </v-icon>
                <span>{{ $t('auth.passwordMinLength') }}</span>
              </div>
              <div class="requirement-line" :class="{ 'requirement-ok': passwordChecks.hasNumber }">
                <v-icon x-small :color="passwordChecks.hasNumber ? 'success' : 'grey'">
                  {{ passwordChecks.hasNumber ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                </v-icon>
                <span>{{ $t('auth.passwordMustIncludeNumber') }}</span>
              </div>
              <div class="requirement-line" :class="{ 'requirement-ok': passwordChecks.hasSpecial }">
                <v-icon x-small :color="passwordChecks.hasSpecial ? 'success' : 'grey'">
                  {{ passwordChecks.hasSpecial ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                </v-icon>
                <span>{{ $t('auth.passwordMustIncludeSpecial') }}</span>
              </div>
            </div>
          </v-col>
          <v-col cols="12">
            <v-text-field
              id="password_verify"
              prepend-icon="lock"
              :append-icon="showRepeatPassword ? 'visibility' : 'visibility_off'"
              :type="showRepeatPassword ? 'text' : 'password'"
              @click:append="showRepeatPassword = !showRepeatPassword"
              v-model="form.passwordVerify"
              :rules="passwordMatchRules"
              :label="$t('user.label.repeatPassword')"
              :placeholder="$t('user.label.repeatPassword')"
              autocomplete="new-password"
            />
          </v-col>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="cancel">{{ $t('common.cancel') }}</v-btn>
        <v-btn color="primary" :loading="loading" :disabled="!valid" @click="submit">
          {{ $t('auth.changePassword') }}
        </v-btn>
      </v-card-actions>
    </template>
  </v-card>
</template>

<script>
import apolloClient from '@/apollo';
import ProfileProvider from '@dracul/user-frontend/src/providers/ProfileProvider';
import { passwordStrengthRule, getPasswordRequirementChecks } from '@/utils/passwordValidation';

const EMAIL_PENDIENTE_SUFFIX = '@pendiente.local';

export default {
  name: 'PasswordFormInvira',
  props: {
    me: { type: Object, default: null },
  },
  data() {
    return {
      loading: false,
      valid: true,
      errorMessage: null,
      showCurrentPassword: false,
      showNewPassword: false,
      showRepeatPassword: false,
      form: {
        email: '',
        currentPassword: null,
        newPassword: null,
        passwordVerify: null,
      },
      changedPassword: false,
      requiredRule: [v => !!v || this.$t('user.validation.required')],
      emailRules: [
        v => !!v || this.$t('user.validation.required'),
        v => !v || /.+@.+\..+/.test(v) || (this.$t('user.validation.emailFormat') || 'Formato de correo inválido'),
      ],
    };
  },
  computed: {
    mustSetEmail() {
      return this.me && this.me.email && String(this.me.email).endsWith(EMAIL_PENDIENTE_SUFFIX);
    },
    newPasswordRules() {
      return [
        v => (v != null && String(v).trim() !== '') || this.$t('user.validation.required'),
        passwordStrengthRule(this),
      ];
    },
    passwordChecks() {
      return getPasswordRequirementChecks(this.form.newPassword);
    },
    passwordMatchRules() {
      return [
        v => !!v || this.$t('user.validation.required'),
        () => (this.form.newPassword === this.form.passwordVerify) || this.$t('user.validation.passwordVerify'),
      ];
    },
  },
  methods: {
    async submit() {
      if (!this.$refs.form.validate()) return;
      this.errorMessage = null;
      this.loading = true;
      try {
        if (this.mustSetEmail && this.form.email && this.form.email.trim()) {
          await apolloClient.mutate({
            mutation: require('../../providers/gql/updateMyEmail.graphql'),
            variables: { email: this.form.email.trim() },
          });
        }
        const response = await ProfileProvider.changePassword(this.form.currentPassword, this.form.newPassword);
        const status = response.data && response.data.changePassword && response.data.changePassword.status;
        if (status) {
          await this.$store.dispatch('fetchMe');
          this.changedPassword = true;
          this.$emit('success');
        } else {
          this.errorMessage = this.$t('auth.badCredentials') || 'Error al cambiar la contraseña';
        }
      } catch (err) {
        const msg = err.graphQLErrors && err.graphQLErrors[0] && err.graphQLErrors[0].message;
        this.errorMessage = msg || err.message || this.$t('auth.badCredentials') || 'Error';
      } finally {
        this.loading = false;
      }
    },
    cancel() {
      this.$emit('close');
      if (this.$refs.form) this.$refs.form.reset();
      this.form.email = '';
      this.form.currentPassword = null;
      this.form.newPassword = null;
      this.form.passwordVerify = null;
    },
  },
};
</script>

<style scoped>
.password-requirements {
  padding-left: 4px;
}
.requirement-line {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 2px;
}
.requirement-line .v-icon {
  flex-shrink: 0;
}
.requirement-line span {
  font-size: 0.75rem;
}
.requirement-ok span {
  color: var(--v-success-base, #4caf50);
}
</style>
